import { useMemo } from "react";
import { useGetIncomeTimeSeriesQuery } from "@/features/accounting/income/api";
import type { BudgetTimeSeries, ProfitLossChartPoint } from "../types/api";
import type { BudgetInterval } from "../utils/period";
import { parseMoney } from "../utils/format";
import { formatChartLabel } from "../utils/chartLabels";

interface UseProfitLossDataParams {
  budgetTimeSeries: BudgetTimeSeries | null | undefined;
  startDate: string;
  endDate: string;
  interval: BudgetInterval | undefined;
  projectId: number | null | undefined;
  locale: string;
}

interface ProfitLossResult {
  chartData: ProfitLossChartPoint[];
  totalRevenue: number;
  totalExpense: number;
  netIncome: number;
  isLoading: boolean;
}

export function useProfitLossData({
  budgetTimeSeries,
  startDate,
  endDate,
  interval,
  projectId,
  locale,
}: UseProfitLossDataParams): ProfitLossResult {
  const {
    data: incomeTimeSeries,
    isLoading: isIncomeLoading,
  } = useGetIncomeTimeSeriesQuery(
    {
      start_date: startDate,
      end_date: endDate,
      interval: interval ?? "month",
      status: "confirmed",
      project_id: projectId ?? undefined,
    },
    { skip: !startDate || !endDate || !interval },
  );

  const isBudgetReady = budgetTimeSeries != null && budgetTimeSeries.points.length > 0;
  const isLoading = isIncomeLoading || !isBudgetReady;

  const { chartData, totalRevenue, totalExpense, netIncome } = useMemo(() => {
    if (!budgetTimeSeries?.points.length) {
      return { chartData: [], totalRevenue: 0, totalExpense: 0, netIncome: 0 };
    }

    const budgetInterval = budgetTimeSeries.interval;
    const budgetPoints = budgetTimeSeries.points;
    const incomePoints = incomeTimeSeries?.points ?? [];

    if (import.meta.env.DEV && incomePoints.length > 0 && budgetPoints.length !== incomePoints.length) {
      console.warn(`[ProfitLoss] Point count mismatch: budget=${budgetPoints.length}, income=${incomePoints.length}`);
    }

    let sumRevenue = 0;
    let sumExpense = 0;

    const data: ProfitLossChartPoint[] = budgetPoints.map(function buildPoint(point, index) {
      const revenue = index < incomePoints.length
        ? parseMoney(incomePoints[index].total)
        : 0;
      const expense = parseMoney(point.total);
      sumRevenue += revenue;
      sumExpense += expense;
      return {
        label: formatChartLabel(point.date, budgetInterval, locale),
        date: point.date,
        revenue,
        expense,
        netIncome: revenue - expense,
      };
    });

    return {
      chartData: data,
      totalRevenue: sumRevenue,
      totalExpense: sumExpense,
      netIncome: sumRevenue - sumExpense,
    };
  }, [budgetTimeSeries, incomeTimeSeries, locale]);

  return { chartData, totalRevenue, totalExpense, netIncome, isLoading };
}
